/**
 * TeamSystem Impact - DMA Main Page
 *
 * Main entry point for the Double Materiality Assessment module.
 * Uses URL-based routing for sub-sections instead of tabs.
 * Year is controlled from the sidebar.
 */

import { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Alert,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  LinearProgress,
  Paper,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// Lazy-load heavy DMA sub-components for bundle splitting
const IRORegistryList = lazy(() => import('./components/IRORegistryList').then(m => ({ default: m.IRORegistryList })));
const IROForm = lazy(() => import('./components/IROForm').then(m => ({ default: m.IROForm })));
const IRODetail = lazy(() => import('./components/IRODetail').then(m => ({ default: m.IRODetail })));
const MaterialityMatrixV2 = lazy(() => import('./components/MaterialityMatrixV2').then(m => ({ default: m.MaterialityMatrixV2 })));
const SetupWizard = lazy(() => import('./components/SetupWizard').then(m => ({ default: m.SetupWizard })));
const GovernancePanel = lazy(() => import('./components/GovernancePanel').then(m => ({ default: m.GovernancePanel })));
const DataCapturePanel = lazy(() => import('./components/DataCapturePanel').then(m => ({ default: m.DataCapturePanel })));
const MyAssessmentsList = lazy(() => import('./components/MyAssessmentsList').then(m => ({ default: m.MyAssessmentsList })));
const CampaignManager = lazy(() => import('./components/CampaignManager').then(m => ({ default: m.CampaignManager })));
const CampaignDetail = lazy(() => import('./components/CampaignDetail').then(m => ({ default: m.CampaignDetail })));
const ProposeIROForm = lazy(() => import('./components/ProposeIROForm').then(m => ({ default: m.ProposeIROForm })));
const AssessmentPrepareDialog = lazy(() => import('./components/AssessmentPrepareDialog').then(m => ({ default: m.AssessmentPrepareDialog })));
const StakeholderAssessmentDialog = lazy(() => import('./components/StakeholderAssessmentDialog').then(m => ({ default: m.StakeholderAssessmentDialog })));
const DMASettings = lazy(() => import('./components/DMASettings').then(m => ({ default: m.DMASettings })));
const MaterialityDashboard = lazy(() => import('./components/MaterialityDashboard').then(m => ({ default: m.MaterialityDashboard })));
import type { AssignedIRO, AssessmentListItem } from './api/stakeholder-assessment.api';
import { setupApi, stakeholderAssessmentApi } from './api';
import type { IRO } from './api/iro.api';
import type { DMASetup, CompletenessStats } from './api/setup.api';
import { useWorkingYear } from '../../store/workingYearStore';
import { useAuthStore } from '../../store/authStore';
import { isReadOnlyRole } from '../../utils/permissions';
import { useUserDmaAssignments } from './hooks/useUserDmaAssignments';
import { logger } from '@/config/logger';

// ============= I18n Labels Helper =============
function useLabels() {
  const { t } = useTranslation();

  return {
    iroCatalog: t('dma.page.iroCatalog'),
    year: t('dma.page.year'),
    stakeholderAssessment: t('dma.page.stakeholderAssessment'),
    activeCampaigns: t('dma.page.activeCampaigns'),
    assignedAssessments: t('dma.page.assignedAssessments'),
    assessmentCampaigns: t('dma.page.assessmentCampaigns'),
    proposeNewIro: t('dma.page.proposeNewIro'),
    materialityMatrix: t('dma.page.materialityMatrix'),
    dmaSetup: t('dma.page.dmaSetup'),
    editSetup: t('dma.page.editSetup'),
    createSetup: t('dma.page.createSetup'),
    createNewSetup: t('dma.page.createNewSetup'),
    noSetupConfigured: t('dma.page.noSetupConfigured'),
    configCompleteness: t('dma.page.configCompleteness'),
    groupCompanies: t('dma.page.groupCompanies'),
    activeGeographies: t('dma.page.activeGeographies'),
    valueChain: t('dma.page.valueChain'),
    stakeholderCategories: t('dma.page.stakeholderCategories'),
    naceCodes: t('dma.page.naceCodes'),
    materialityThresholds: t('dma.page.materialityThresholds'),
    impactScoreThreshold: t('dma.page.impactScoreThreshold'),
    financialScoreThreshold: t('dma.page.financialScoreThreshold'),
    noSetupWarning: t('dma.page.noSetupWarning'),
    impact: t('dma.page.impact'),
    financial: t('dma.page.financial'),
    both: t('dma.page.both'),
    iro: 'IRO',
    deadline: t('dma.page.deadline'),
    companies: t('dma.page.companies'),
    geographies: t('dma.page.geographies'),
    stakeholders: t('dma.page.stakeholders'),
    nace: 'NACE',
  };
}

// ============= IRO Catalog View =============
function IROCatalogView({ assessmentYear }: { assessmentYear: number }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Support ?iro=<id> query param to open detail view directly (e.g. from "open in new tab")
  const searchParams = new URLSearchParams(location.search);
  const iroIdFromUrl = searchParams.get('iro');

  const [viewMode, setViewMode] = useState<'list' | 'detail' | 'form'>(iroIdFromUrl ? 'detail' : 'list');
  const [selectedIRO, setSelectedIRO] = useState<IRO | null>(null);
  const [selectedIROId, setSelectedIROId] = useState<string | null>(iroIdFromUrl);
  const [detailInitialTab, setDetailInitialTab] = useState(0);
  const [createPresets, setCreatePresets] = useState<{ esrsStandard?: string; esrsSubTopic?: string } | undefined>(undefined);

  const handleCreateNew = (presets?: { esrsStandard?: string; esrsSubTopic?: string }) => {
    setSelectedIRO(null);
    setCreatePresets(presets);
    setViewMode('form');
  };

  const handleViewIRO = (iro: IRO) => {
    if (!iro._id) {
      logger.error('IRO missing _id:', iro);
      return;
    }
    setSelectedIROId(iro._id);
    setDetailInitialTab(0);
    setViewMode('detail');
  };

  const handleAssessIRO = (iro: IRO) => {
    if (!iro._id) {
      logger.error('IRO missing _id:', iro);
      return;
    }
    // Navigate to assessment page with this IRO
    navigate(`/dma/assessment?iro=${iro._id}`);
  };

  const handleEditIRO = (iro: IRO) => {
    setSelectedIRO(iro);
    setViewMode('form');
  };

  const handleSaveIRO = (savedIRO: IRO) => {
    if (!savedIRO._id) {
      logger.error('Saved IRO missing _id:', savedIRO);
      setViewMode('list');
      return;
    }
    setSelectedIROId(savedIRO._id);
    setViewMode('detail');
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedIRO(null);
    setSelectedIROId(null);
    setCreatePresets(undefined);
  };

  switch (viewMode) {
    case 'detail':
      if (!selectedIROId) return null;
      return (
        <IRODetail
          iroId={selectedIROId}
          onBack={handleBackToList}
          onEdit={handleEditIRO}
          onUpdate={() => {}}
          initialTab={detailInitialTab}
        />
      );

    case 'form':
      return (
        <IROForm
          iro={selectedIRO || undefined}
          assessmentYear={assessmentYear}
          onSave={handleSaveIRO}
          onCancel={handleBackToList}
          presets={createPresets}
        />
      );

    case 'list':
    default:
      return (
        <IRORegistryList
          assessmentYear={assessmentYear}
          onCreateNew={handleCreateNew}
          onView={handleViewIRO}
          onEdit={handleEditIRO}
          onAssess={handleAssessIRO}
        />
      );
  }
}

// ============= Assessment View =============
function AssessmentView({ assessmentYear }: { assessmentYear: number }) {
  const labels = useLabels();
  const { t } = useTranslation();
  const utente = useAuthStore((s) => s.utente);
  const isAdmin = utente?.ruolo === 'amministratore' || utente?.ruolo === 'responsabile_esg';
  const [prepareDialogOpen, setPrepareDialogOpen] = useState(false);
  const [selectedIROForAssessment, setSelectedIROForAssessment] = useState<AssignedIRO | null>(null);
  const [allAssignedIROs, setAllAssignedIROs] = useState<AssignedIRO[]>([]);
  const [assessmentDialogOpen, setAssessmentDialogOpen] = useState(false);
  const [listRefreshKey, setListRefreshKey] = useState(0);

  const prepareAssessmentLabel = t('dma.page.prepareAssessment');

  // Fetch all assigned IROs for the card flow — filtrati per l'anno di lavoro
  // corrente (altrimenti comparirebbero IRO assegnati in altri anni).
  useEffect(() => {
    loadAssignedIROs();
    // loadAssignedIROs è definita inline e dipende solo da assessmentYear (già in deps);
    // includerla ricreerebbe l'effect a ogni render → fetch in loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listRefreshKey, assessmentYear]);

  const loadAssignedIROs = async () => {
    try {
      const result = await stakeholderAssessmentApi.getMyAssignedIROs(false, assessmentYear);
      setAllAssignedIROs(result.assigned);
    } catch {
      setAllAssignedIROs([]);
    }
  };

  const handleSelectAssessment = (item: AssessmentListItem) => {
    // Convert AssessmentListItem to AssignedIRO for the dialog
    const assignedIRO: AssignedIRO = {
      iroId: item.iroId,
      iroCode: item.iroCode,
      iroName: item.iroName,
      esrsStandard: item.esrsStandard,
      assessmentType: item.assessmentType,
      status: item.status === 'revised' ? 'completed' : item.status,
      myAssessment: {
        _id: item._id,
        iroId: item.iroId,
        aziendaId: '',
        assessorId: item.assessorId,
        assessorName: item.assessorName,
        assessorRole: item.assessorRole,
        assessmentType: item.assessmentType,
        weight: item.weight,
        impactAssessment: item.impactAssessment || undefined,
        financialAssessment: item.financialAssessment || undefined,
        status: item.status,
        submittedAt: item.submittedAt || undefined,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
      },
    };
    setSelectedIROForAssessment(assignedIRO);
    setAssessmentDialogOpen(true);
  };

  const [onBehalfOfUserId, setOnBehalfOfUserId] = useState<string | undefined>(undefined);
  const [onBehalfOfUserName, setOnBehalfOfUserName] = useState<string | undefined>(undefined);

  const handleStartPendingAssessment = (assignedIRO: AssignedIRO) => {
    setOnBehalfOfUserId(undefined);
    setOnBehalfOfUserName(undefined);
    setSelectedIROForAssessment(assignedIRO);
    setAssessmentDialogOpen(true);
  };

  const handleFillOnBehalf = (assignedIRO: AssignedIRO) => {
    // Admin fills on behalf of another stakeholder
    setOnBehalfOfUserId(assignedIRO.assessorId);
    setOnBehalfOfUserName(assignedIRO.assessorName);
    setSelectedIROForAssessment(assignedIRO);
    setAssessmentDialogOpen(true);
  };

  const handleFillOnBehalfFromDialog = (stakeholderId: string, stakeholderName: string, iroIds: string[], assessmentType: 'impact' | 'financial' | 'both') => {
    if (iroIds.length === 0) return;

    // Build AssignedIRO objects for the selected IROs
    const assignedIROsForBehalf: AssignedIRO[] = iroIds.map(iroId => {
      const iro = allAssignedIROs.find(a => a.iroId === iroId);
      return {
        iroId,
        iroCode: iro?.iroCode || iroId,
        iroName: iro?.iroName || iroId,
        esrsStandard: (iro?.esrsStandard || 'E1') as AssignedIRO['esrsStandard'],
        assessmentType,
        status: 'pending' as const,
      };
    });

    // Set on-behalf state
    setOnBehalfOfUserId(stakeholderId);
    setOnBehalfOfUserName(stakeholderName);

    // Update allAssignedIROs for card navigation if multiple IROs
    if (assignedIROsForBehalf.length > 1) {
      setAllAssignedIROs(assignedIROsForBehalf);
    }

    // Open the assessment dialog with the first IRO
    setSelectedIROForAssessment(assignedIROsForBehalf[0]);
    setAssessmentDialogOpen(true);
  };

  const handleAssessmentSuccess = () => {
    setAssessmentDialogOpen(false);
    setSelectedIROForAssessment(null);
    setListRefreshKey(k => k + 1);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">{labels.stakeholderAssessment} - {labels.year} {assessmentYear}</Typography>
        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setPrepareDialogOpen(true)}
          >
            {prepareAssessmentLabel}
          </Button>
        )}
      </Box>

      {/* Assessment Prepare Dialog (admin only) */}
      <AssessmentPrepareDialog
        open={prepareDialogOpen}
        onClose={() => setPrepareDialogOpen(false)}
        onSuccess={() => setListRefreshKey(k => k + 1)}
        onFillOnBehalf={handleFillOnBehalfFromDialog}
      />

      {/* Assessments Table */}
      <MyAssessmentsList
        assessmentYear={assessmentYear}
        onSelectAssessment={handleSelectAssessment}
        onStartPendingAssessment={handleStartPendingAssessment}
        onFillOnBehalf={handleFillOnBehalf}
        refreshTrigger={listRefreshKey}
      />

      {/* Stakeholder Assessment Dialog */}
      <StakeholderAssessmentDialog
        assignedIRO={selectedIROForAssessment}
        open={assessmentDialogOpen}
        onClose={() => {
          setAssessmentDialogOpen(false);
          setSelectedIROForAssessment(null);
          setOnBehalfOfUserId(undefined);
          setOnBehalfOfUserName(undefined);
        }}
        onSuccess={handleAssessmentSuccess}
        allAssignedIROs={allAssignedIROs.length > 1 ? allAssignedIROs : undefined}
        onBehalfOfUserId={onBehalfOfUserId}
        onBehalfOfUserName={onBehalfOfUserName}
      />
    </Box>
  );
}

// ============= Campaigns View (Admin) =============
function CampaignsView({ assessmentYear }: { assessmentYear: number }) {
  const location = useLocation();
  const navigate = useNavigate();
  const labels = useLabels();
  const searchParams = new URLSearchParams(location.search);
  const campaignIdFromUrl = searchParams.get('id');

  const [viewMode, setViewMode] = useState<'list' | 'detail' | 'propose'>(
    campaignIdFromUrl ? 'detail' : 'list'
  );
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(
    campaignIdFromUrl
  );

  // Update view when URL changes
  useEffect(() => {
    if (campaignIdFromUrl) {
      setSelectedCampaignId(campaignIdFromUrl);
      setViewMode('detail');
    }
  }, [campaignIdFromUrl]);

  const handleViewCampaign = (campaignId: string) => {
    setSelectedCampaignId(campaignId);
    setViewMode('detail');
    navigate(`/dma/campaigns?id=${campaignId}`);
  };

  const handleBack = () => {
    setViewMode('list');
    setSelectedCampaignId(null);
    navigate('/dma/campaigns');
  };

  switch (viewMode) {
    case 'detail':
      if (!selectedCampaignId) return null;
      return (
        <CampaignDetail
          campaignId={selectedCampaignId}
          onBack={handleBack}
        />
      );

    case 'propose':
      if (!selectedCampaignId) return null;
      return (
        <Box>
          <Typography variant="h5" component="h1" sx={{ mb: 3 }}>{labels.proposeNewIro}</Typography>
          <ProposeIROForm
            campaignId={selectedCampaignId}
            onSuccess={handleBack}
            onCancel={handleBack}
          />
        </Box>
      );

    case 'list':
    default:
      return (
        <Box>
          <Typography variant="h5" component="h1" sx={{ mb: 3 }}>{labels.assessmentCampaigns} - {labels.year} {assessmentYear}</Typography>
          <CampaignManager onViewCampaign={handleViewCampaign} />
        </Box>
      );
  }
}

// ============= Materiality Dashboard View =============
function MaterialityView({ assessmentYear }: { assessmentYear: number }) {
  return <MaterialityDashboard assessmentYear={assessmentYear} />;
}

// ============= Materiality Matrix View =============
function MatrixView({ assessmentYear }: { assessmentYear: number }) {
  const navigate = useNavigate();
  const labels = useLabels();

  const handleSelectIRO = (iroId: string) => {
    navigate(`/dma/catalog?iro=${iroId}`);
  };

  return (
    <Box>
      <Typography variant="h5" component="h1" sx={{ mb: 3 }}>{labels.materialityMatrix} - {labels.year} {assessmentYear}</Typography>
      <MaterialityMatrixV2 assessmentYear={assessmentYear} onSelectIRO={handleSelectIRO} />
    </Box>
  );
}

// ============= Setup View (Admin only) =============
function SetupView({ assessmentYear, setup, onSetupChange }: {
  assessmentYear: number;
  setup: DMASetup | null;
  onSetupChange: () => void;
}) {
  const labels = useLabels();
  const utente = useAuthStore((s) => s.utente);
  const isReadOnly = isReadOnlyRole(utente?.ruolo);
  const [viewMode, setViewMode] = useState<'view' | 'wizard'>('view');
  const [completeness, setCompleteness] = useState<CompletenessStats | null>(null);

  useEffect(() => {
    if (setup?._id) {
      setupApi.getCompleteness(setup._id).then(setCompleteness).catch(() => setCompleteness(null));
    } else {
      setCompleteness(null);
    }
  }, [setup]);

  const handleSetupComplete = () => {
    setViewMode('view');
    onSetupChange();
  };

  if (viewMode === 'wizard') {
    return (
      <SetupWizard
        existingSetup={setup || undefined}
        onComplete={handleSetupComplete}
        onCancel={() => setViewMode('view')}
      />
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">{labels.dmaSetup} {assessmentYear}</Typography>
        {!isReadOnly && (
          <Button
            variant="contained"
            startIcon={setup ? <EditIcon /> : <AddIcon />}
            onClick={() => setViewMode('wizard')}
          >
            {setup ? labels.editSetup : labels.createSetup}
          </Button>
        )}
      </Box>

      {!setup ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary" paragraph>
            {labels.noSetupConfigured} {assessmentYear}.
          </Typography>
          {!isReadOnly && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setViewMode('wizard')}
            >
              {labels.createNewSetup}
            </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {/* Completeness Card */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Typography variant="subtitle2">{labels.configCompleteness}</Typography>
                <Chip
                  size="small"
                  label={`${completeness?.percentage || 0}%`}
                  color={completeness?.percentage === 100 ? 'success' : 'warning'}
                />
              </Box>
              <LinearProgress
                variant="determinate"
                value={completeness?.percentage || 0}
                sx={{ mb: 2 }}
              />
              {completeness && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip size="small" label={labels.companies} color={completeness.details.groupCompanies ? 'success' : 'default'} variant={completeness.details.groupCompanies ? 'filled' : 'outlined'} />
                  <Chip size="small" label={labels.geographies} color={completeness.details.activeCountries ? 'success' : 'default'} variant={completeness.details.activeCountries ? 'filled' : 'outlined'} />
                  <Chip size="small" label={labels.valueChain} color={completeness.details.valueChain ? 'success' : 'default'} variant={completeness.details.valueChain ? 'filled' : 'outlined'} />
                  <Chip size="small" label={labels.stakeholders} color={completeness.details.stakeholders ? 'success' : 'default'} variant={completeness.details.stakeholders ? 'filled' : 'outlined'} />
                  <Chip size="small" label={labels.nace} color={completeness.details.naceCodes ? 'success' : 'default'} variant={completeness.details.naceCodes ? 'filled' : 'outlined'} />
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Summary Cards */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>{labels.groupCompanies}</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '2rem' }}>{setup.groupCompanies?.length || 0}</Typography>
                <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {setup.groupCompanies?.slice(0, 3).map((c) => <Chip key={c.id} size="small" label={c.name} />)}
                  {(setup.groupCompanies?.length || 0) > 3 && <Chip size="small" label={`+${setup.groupCompanies!.length - 3}`} />}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>{labels.activeGeographies}</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '2rem' }}>{setup.activeCountries?.length || 0}</Typography>
                <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {setup.activeCountries?.map((c) => <Chip key={c} size="small" label={c} />)}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>{labels.valueChain}</Typography>
                {(() => {
                  const vc = setup.valueChainConfig;
                  if (Array.isArray(vc)) {
                    return (
                      <>
                        <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '2rem' }}>{vc.length || 0}</Typography>
                        <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {vc.map((v: any) => <Chip key={v.position} size="small" label={v.position} />)}
                        </Box>
                      </>
                    );
                  } else if (vc && typeof vc === 'object') {
                    const positions = ['upstream', 'operations', 'downstream'].filter((p) => (vc as any)[p]?.enabled);
                    return (
                      <>
                        <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '2rem' }}>{positions.length}</Typography>
                        <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {positions.map((p) => <Chip key={p} size="small" label={p} />)}
                        </Box>
                      </>
                    );
                  }
                  return <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '2rem' }}>0</Typography>;
                })()}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>{labels.stakeholderCategories}</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '2rem' }}>{setup.stakeholderCategories?.length || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>{labels.naceCodes}</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '2rem' }}>{setup.naceCodes?.length || 0}</Typography>
                <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {setup.naceCodes?.filter((n) => n.isPrimary).map((n) => <Chip key={n.code} size="small" label={n.code} color="primary" />)}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Materiality Thresholds */}
          {setup.materialityThresholds && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>{labels.materialityThresholds}</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2">{labels.impactScoreThreshold}</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '1.5rem' }}>{setup.materialityThresholds.impactScore}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">{labels.financialScoreThreshold}</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '1.5rem' }}>{setup.materialityThresholds.financialScore}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
}

// ============= Main DMA Page =============
export function DMAPage() {
  const { year: assessmentYear } = useWorkingYear();
  const labels = useLabels();
  const { t } = useTranslation();
  const utente = useAuthStore((s) => s.utente);
  const { hasDmaAssessments, loading: dmaLoading } = useUserDmaAssignments();
  const [setup, setSetup] = useState<DMASetup | null>(null);
  const [setupLoading, setSetupLoading] = useState(true);
  const location = useLocation();

  const isContributore = utente?.ruolo === 'contributore';

  useEffect(() => {
    loadSetup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentYear]);

  const loadSetup = async () => {
    try {
      setSetupLoading(true);
      const activeSetup = await setupApi.getByYear(assessmentYear);
      setSetup(activeSetup);
    } catch (err) {
      setSetup(null);
    } finally {
      setSetupLoading(false);
    }
  };

  // Contributore senza assessment DMA → schermata "nessuna attività"
  if (isContributore && !dmaLoading && !hasDmaAssessments) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 10, gap: 2 }}>
        <Typography variant="h6" color="text.secondary">
          {t('dma.page.noAssessmentsAssigned')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('dma.page.contactAdminAssignment')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Setup Warning - show only when no setup and not on setup page (admin only) */}
      {!isContributore && !setupLoading && !setup && !location.pathname.includes('/dma/setup') && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {labels.noSetupWarning.replace('{year}', String(assessmentYear))}
        </Alert>
      )}

      {/* Routes */}
      <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}>
      {isContributore ? (
        // Contributore con assessment: accesso solo alla pagina Valutazioni
        <Routes>
          <Route index element={<Navigate to="assessment" replace />} />
          <Route path="assessment" element={<AssessmentView assessmentYear={assessmentYear} />} />
          <Route path="*" element={<Navigate to="assessment" replace />} />
        </Routes>
      ) : (
        // Admin / responsabile ESG: accesso completo
        <Routes>
          <Route index element={<Navigate to="catalog" replace />} />
          <Route path="catalog" element={<IROCatalogView assessmentYear={assessmentYear} />} />
          <Route path="assessment" element={<AssessmentView assessmentYear={assessmentYear} />} />
          <Route path="campaigns" element={<CampaignsView assessmentYear={assessmentYear} />} />
          <Route path="materiality" element={<MaterialityView assessmentYear={assessmentYear} />} />
          <Route path="matrix" element={<MatrixView assessmentYear={assessmentYear} />} />
          <Route path="governance" element={<GovernancePanel />} />
          <Route path="data-capture" element={<DataCapturePanel assessmentYear={assessmentYear} />} />
          <Route path="setup" element={<SetupView assessmentYear={assessmentYear} setup={setup} onSetupChange={loadSetup} />} />
          <Route path="settings" element={<DMASettings assessmentYear={assessmentYear} />} />
          <Route path="*" element={<Navigate to="catalog" replace />} />
        </Routes>
      )}
      </Suspense>
    </Box>
  );
}
